"use client"

import type { Offer } from "@local/offer/src/types"
import { firstNonEmpty } from "@local/utils/src/firstNonEmpty"
import { getCdnUrl } from "@local/utils/src/getCdnUrl"
import { getT } from "@local/i18n/src/t"
import { getLanguageForMarket } from "@local/i18n/src/languages"
import { a11y } from "@local/i18n/src/catalogs/a11y"
import Image from "next/image"
import { useSearchParams } from "next/navigation"
import { Fragment, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import CssCarousel from "../../ui/CssCarousel"
import { Modal } from "../../ui/Modal"

export const CarouselWrapper = ({
  slides,
  product,
  includeVariantImage,
  priority,
  market,
}: {
  slides: Offer.Highlight["slides"]
  product: Offer.PinProduct | undefined
  includeVariantImage?: boolean
  /** Eager-load the first slide when the carousel is above the fold (LCP candidate) */
  priority?: boolean
  /** Market slug: localizes the carousel arrow aria-labels. */
  market?: string
}) => {
  const t = getT(getLanguageForMarket(market), { a11y })
  const [showModal, setShowModal] = useState(false)
  const [carouselSelectedItem, setCarouselSelectedItem] = useState<number>(0)

  const searchParams = useSearchParams()
  const itemId = searchParams?.get("item_id")

  const actualSlides = useMemo(() => {
    if (!itemId || !product || !includeVariantImage) {
      return slides
    }

    const allSkus: Offer.PinProductSkuList["skus"] =
      product.type === "BUNDLE"
        ? product.skuLists.flatMap((skuList) => skuList.skus)
        : product.skus

    const matchingSku = allSkus.find(
      (sku) =>
        sku.legacyId === itemId ||
        sku.attributes.some(
          (attr) => attr.name === "payloadId" && attr.value === itemId
        ) ||
        sku.id === itemId ||
        sku.name === itemId
    )

    const primaryImage = matchingSku?.media.default?.[0]

    if (!primaryImage) {
      return slides
    }

    const imageAlreadyExists = slides?.some(
      (slide) => slide.image?.url === primaryImage.url
    )

    if (imageAlreadyExists) {
      return slides
    }

    const newSlide = {
      // title stays undefined: itemId is an opaque id and title is only ever
      // consumed as an a11y alt fallback, so an absent title lets the localized
      // string win instead of announcing the raw id.
      title: undefined as string | undefined,
      id: itemId,
      image: { ...primaryImage, instanceId: itemId },
    }

    return [newSlide, ...(slides ?? [])]
  }, [itemId, slides, product, includeVariantImage])

  return (
    <div>
      {/* Pass carouselSelectedItem as initialSlide to maintain position */}
      <CssCarousel
        showArrows={true}
        showThumbs={true}
        onClick={() => setShowModal(true)}
        afterSlide={setCarouselSelectedItem}
        market={market}
        slidesPerView={{
          base: 1,
          sm: 1,
          md: 1,
        }}
        // initialSlide={initialSlide}
      >
        {actualSlides?.map((slide, i) => (
          <Image
            key={`product-modal-${slide?.image?.url}-${i}`}
            src={getCdnUrl({ src: slide?.image?.url ?? "", width: 1024 })}
            alt={firstNonEmpty(
              [slide?.image?.alt, slide?.title],
              t("a11y.slide", { n: i + 1 })
            )}
            width={1024}
            height={1024}
            sizes="(max-width: 768px) 100vw, 50vw"
            className="cursor-pointer"
            priority={!!priority && i === 0}
          />
        ))}
      </CssCarousel>

      <>
        {showModal ? (
          createPortal(
            <Modal
              open={true}
              toggle={() => {
                setShowModal(!showModal)
                //trackAction?
              }}
            >
              <CssCarousel
                showArrows={true}
                showThumbs={true}
                onClick={() => setShowModal(true)}
                initialSlide={carouselSelectedItem}
                market={market}
                slidesPerView={{
                  base: 1,
                  sm: 1,
                  md: 1,
                }}
              >
                {actualSlides?.map((slide, i) => (
                  <Image
                    key={`product-modal-${slide?.image?.url}-${i}`}
                    src={getCdnUrl({
                      src: slide?.image?.url ?? "",
                      width: 1024,
                    })}
                    alt={firstNonEmpty(
                      [slide?.image?.alt, slide?.title],
                      t("a11y.productModalImage", { n: i + 1 })
                    )}
                    width={1024}
                    height={1024}
                    sizes="100vw"
                    className="cursor-pointer"
                  />
                ))}
              </CssCarousel>
            </Modal>,
            document.body
          )
        ) : (
          <Fragment />
        )}
      </>
    </div>
  )
}
